import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClipboardList,
  Search,
  MoreVertical,
  Eye,
  Trash2,
  MapPin,
  User,
  X,
  Download,
  Phone,
  DollarSign,
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
  Loader
} from 'lucide-react';
import { formatDate, formatPrice, exportToCSV } from '../utils/helpers';
import { requestsService } from '../services';
import { useToast } from '../context/ToastContext';
import DropdownMenu from '../components/DropdownMenu';
import './RequestsManagementPage.css';

const RequestsManagementPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);

  // API state
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, closed: 0, expired: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, total: 1, count: 0 });
  const currentPage = pagination.current;

  // Fetch requests from API
  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentPage,
        limit: 20
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (sortBy === 'oldest') {
        params.sortBy = 'createdAt';
        params.sortOrder = 'asc';
      } else {
        params.sortBy = 'createdAt';
        params.sortOrder = 'desc';
      }

      const response = await requestsService.getAll(params);

      if (response.success) {
        setRequests(response.data.requests || []);
        setStats(response.data.stats || { total: 0, active: 0, closed: 0, expired: 0 });
        setPagination(response.data.pagination || { current: 1, total: 1, count: 0 });
      } else {
        setError(response.message || t('errors.fetchFailed'));
      }
    } catch (err) {
      setError(err.message || t('errors.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, sortBy, currentPage, t]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, current: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleViewRequest = useCallback((request) => {
    setSelectedRequest(request);
    setShowRequestModal(true);
    setActiveMenu(null);
  }, []);

  const handleUpdateStatus = useCallback(async (requestId, newStatus) => {
    try {
      const response = await requestsService.updateStatus(requestId, newStatus);
      if (response.success) {
        fetchRequests();
        if (showRequestModal) setShowRequestModal(false);
      } else {
        toast.error(response.message || t('errors.updateFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.updateFailed'));
    }
    setActiveMenu(null);
  }, [fetchRequests, showRequestModal, t, toast]);

  const handleDelete = useCallback(async (requestId) => {
    if (window.confirm(t('requests.confirmDelete'))) {
      try {
        const response = await requestsService.delete(requestId);
        if (response.success) {
          fetchRequests();
        } else {
          toast.error(response.message || t('errors.deleteFailed'));
        }
      } catch (err) {
        toast.error(err.message || t('errors.deleteFailed'));
      }
    }
    setActiveMenu(null);
  }, [fetchRequests, t, toast]);

  const handleExport = useCallback(() => {
    const requestTypes = {
      villa: 'فيلا',
      apartment: 'شقة',
      land: 'أرض',
      building: 'عمارة',
      commercial: 'تجاري',
      duplex: 'دوبلكس'
    };
    
    const columns = [
      { key: 'clientName', label: 'اسم العميل' },
      { key: 'clientPhone', label: 'رقم الهاتف' },
      { key: 'requestType', label: 'نوع الطلب', formatter: (v) => requestTypes[v] || v },
      { key: 'city', label: 'المدينة' },
      { key: 'district', label: 'الحي' },
      { key: 'budgetMin', label: 'الميزانية (من)', formatter: formatPrice },
      { key: 'budgetMax', label: 'الميزانية (إلى)', formatter: formatPrice },
      { key: 'status', label: 'الحالة' },
      { key: 'user.name', label: 'المسوق' },
      { key: 'createdAt', label: 'تاريخ الإضافة', formatter: formatDate }
    ];
    
    if (requests.length === 0) {
      toast.warning('لا توجد بيانات للتصدير');
      return;
    }
    
    const success = exportToCSV(requests, columns, 'requests');
    if (success) {
      toast.success(t('common.exportSuccess'));
    } else {
      toast.error(t('errors.exportFailed'));
    }
  }, [requests, t, toast]);

  const getStatusBadge = (status) => {
    const badges = {
      active: { label: t('requests.active'), class: 'badge-success', icon: CheckCircle },
      new: { label: t('requests.new'), class: 'badge-info', icon: Clock },
      contacted: { label: t('requests.contacted'), class: 'badge-warning', icon: Phone },
      completed: { label: t('requests.completed'), class: 'badge-success', icon: CheckCircle },
      closed: { label: t('requests.closed'), class: 'badge-secondary', icon: XCircle },
      expired: { label: t('requests.expired'), class: 'badge-danger', icon: XCircle }
    };
    return badges[status] || badges.active;
  };

  const getRequestType = (type) => {
    const types = {
      buy: 'شراء',
      rent: 'إيجار'
    };
    return types[type] || type;
  };

  const getPropertyType = (type) => {
    const types = {
      villa: t('offers.villa'),
      apartment: t('offers.apartment'),
      land: t('offers.land'),
      building: t('offers.building'),
      duplex: t('offers.duplex'),
      commercial: t('offers.commercial'),
      floor: 'دور',
      room: 'غرفة'
    };
    return types[type] || type;
  };

  // Map API request to display format
  const mapRequest = (request) => ({
    ...request,
    id: request._id,
    clientName: request.clientName || request.title || 'طلب عقاري',
    clientPhone: request.clientPhone || request.phone || '-',
    requestType: request.propertyType,
    marketer: request.user || { name: 'غير معروف' },
    // budget is a single number from backend
    budgetValue: request.budget || request.budgetMax || request.budgetMin || 0
  });

  const mappedRequests = requests.map(mapRequest);

  return (
    <div className="requests-management-page">
      <div className="page-header">
        <div className="header-title">
          <ClipboardList size={28} />
          <div>
            <h1>{t('requests.management')}</h1>
            <span className="count">{stats.total} {t('requests.request')}</span>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={handleExport}>
            <Download size={18} />
            {t('common.export')}
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="quick-stats">
        <div 
          className={`quick-stat ${statusFilter === 'all' ? 'selected' : ''}`} 
          onClick={() => setStatusFilter('all')}
        >
          <span className="stat-number">{stats.total}</span>
          <span className="stat-label">{t('requests.total')}</span>
        </div>
        <div 
          className={`quick-stat active ${statusFilter === 'active' ? 'selected' : ''}`}
          onClick={() => setStatusFilter('active')}
        >
          <span className="stat-number">{stats.active}</span>
          <span className="stat-label">{t('requests.active')}</span>
        </div>
        <div 
          className={`quick-stat closed ${statusFilter === 'closed' ? 'selected' : ''}`}
          onClick={() => setStatusFilter('closed')}
        >
          <span className="stat-number">{stats.closed}</span>
          <span className="stat-label">{t('requests.closed')}</span>
        </div>
        <div 
          className={`quick-stat expired ${statusFilter === 'expired' ? 'selected' : ''}`}
          onClick={() => setStatusFilter('expired')}
        >
          <span className="stat-number">{stats.expired}</span>
          <span className="stat-label">{t('requests.expired')}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="data-section">
        <div className="filters-row">
          <div className="search-wrapper">
            <Search size={20} />
            <input
              type="text"
              className="form-input"
              placeholder={t('requests.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">{t('common.allStatus')}</option>
            <option value="active">{t('requests.active')}</option>
            <option value="contacted">{t('requests.contacted')}</option>
            <option value="completed">{t('requests.completed')}</option>
            <option value="closed">{t('requests.closed')}</option>
            <option value="expired">{t('requests.expired')}</option>
          </select>
          <select
            className="filter-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="newest">الأحدث أولاً</option>
            <option value="oldest">الأقدم أولاً</option>
          </select>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-state">
            <Loader size={32} className="spin" />
            <p>{t('common.loading')}</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="error-state">
            <p>{error}</p>
            <button className="btn btn-primary" onClick={fetchRequests}>
              {t('common.retry')}
            </button>
          </div>
        )}

        {/* Requests Table */}
        {!loading && !error && (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('requests.client')}</th>
                  <th>{t('requests.type')}</th>
                  <th>{t('requests.location')}</th>
                  <th>{t('requests.budget')}</th>
                  <th>{t('requests.marketer')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('common.date')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {mappedRequests.map((request) => {
                  const statusBadge = getStatusBadge(request.status);
                  return (
                    <tr key={request.id}>
                      <td>
                        <div className="client-cell">
                          <span className="client-name">{request.clientName}</span>
                          {request.clientPhone && request.clientPhone !== '-' && (
                            <span className="client-phone">
                              <Phone size={12} />
                              {request.clientPhone}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="type-badge">
                          {getRequestType(request.requestType)}
                        </span>
                      </td>
                      <td>
                        <div className="location-cell">
                          <MapPin size={14} />
                          <span>{request.city || '-'} {request.district ? `- ${request.district}` : ''}</span>
                        </div>
                      </td>
                      <td>
                        <div className="budget-cell">
                          <DollarSign size={14} />
                          <span>
                            {formatPrice(request.budgetValue)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="marketer-cell">
                          <User size={14} />
                          <span>{request.marketer?.name || 'غير معروف'}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${statusBadge.class}`}>
                          {statusBadge.label}
                        </span>
                      </td>
                      <td>
                        <div className="date-cell">
                          <Calendar size={14} />
                          {formatDate(request.createdAt)}
                        </div>
                      </td>
                      <td>
                        <div className="actions-cell">
                          <DropdownMenu
                            isOpen={activeMenu === request.id}
                            onClose={() => setActiveMenu(null)}
                            trigger={
                              <button
                                className="action-btn-icon"
                                onClick={() => setActiveMenu(activeMenu === request.id ? null : request.id)}
                              >
                                <MoreVertical size={18} />
                              </button>
                            }
                          >
                            <button onClick={() => { handleViewRequest(request); setActiveMenu(null); }}>
                              <Eye size={16} />
                              {t('common.view')}
                            </button>
                            {request.status === 'active' && (
                              <>
                                <button onClick={() => { handleUpdateStatus(request.id, 'contacted'); setActiveMenu(null); }}>
                                  <Phone size={16} />
                                  {t('requests.markContacted')}
                                </button>
                                <button onClick={() => { handleUpdateStatus(request.id, 'completed'); setActiveMenu(null); }}>
                                  <CheckCircle size={16} />
                                  {t('requests.markCompleted')}
                                </button>
                              </>
                            )}
                            {request.status === 'contacted' && (
                              <button onClick={() => { handleUpdateStatus(request.id, 'completed'); setActiveMenu(null); }}>
                                <CheckCircle size={16} />
                                {t('requests.markCompleted')}
                              </button>
                            )}
                            <button className="danger" onClick={() => { handleDelete(request.id); setActiveMenu(null); }}>
                              <Trash2 size={16} />
                              {t('common.delete')}
                            </button>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && mappedRequests.length === 0 && (
          <div className="empty-state">
            <ClipboardList size={48} />
            <h3>{t('requests.noRequests')}</h3>
            <p>{t('requests.noRequestsDescription')}</p>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && pagination.total > 1 && (
          <div className="pagination">
            <button
              className="btn btn-outline"
              disabled={pagination.current === 1}
              onClick={() => setPagination(prev => ({ ...prev, current: prev.current - 1 }))}
            >
              {t('common.previous')}
            </button>
            <span className="pagination-info">
              {t('common.page')} {pagination.current} {t('common.of')} {pagination.total}
            </span>
            <button
              className="btn btn-outline"
              disabled={pagination.current === pagination.total}
              onClick={() => setPagination(prev => ({ ...prev, current: prev.current + 1 }))}
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>

      {/* Request Details Modal */}
      {showRequestModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => setShowRequestModal(false)}>
          <div className="modal request-modal large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{t('requests.requestDetails')}</h2>
              <button className="modal-close" onClick={() => setShowRequestModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="request-header-detail">
                <h3>{selectedRequest.clientName}</h3>
                <div className="badges-row">
                  <span className={`badge ${getStatusBadge(selectedRequest.status).class}`}>
                    {getStatusBadge(selectedRequest.status).label}
                  </span>
                  <span className={`badge ${selectedRequest.requestType === 'buy' ? 'badge-primary' : 'badge-purple'}`}>
                    {selectedRequest.requestType === 'buy' ? 'شراء' : 'إيجار'}
                  </span>
                </div>
              </div>

              {/* Client Info */}
              <div className="section-divider">
                <h4>معلومات العميل</h4>
              </div>
              <div className="request-details-grid">
                {selectedRequest.clientPhone && selectedRequest.clientPhone !== '-' && (
                  <div className="detail-item">
                    <span className="detail-label">{t('requests.phone')}</span>
                    <span className="detail-value" dir="ltr">{selectedRequest.clientPhone}</span>
                  </div>
                )}
                {selectedRequest.clientSeriousness && (
                  <div className="detail-item">
                    <span className="detail-label">جدية العميل</span>
                    <span className={`detail-value seriousness-${selectedRequest.clientSeriousness}`}>
                      {selectedRequest.clientSeriousness === 'very_interested' ? 'مهتم جداً' :
                       selectedRequest.clientSeriousness === 'interested' ? 'مهتم' :
                       selectedRequest.clientSeriousness === 'potential' ? 'محتمل' : '-'}
                    </span>
                  </div>
                )}
                {selectedRequest.lastContact && (
                  <div className="detail-item">
                    <span className="detail-label">آخر تواصل</span>
                    <span className="detail-value">{formatDate(selectedRequest.lastContact)}</span>
                  </div>
                )}
              </div>

              {/* Request Details */}
              <div className="section-divider">
                <h4>تفاصيل الطلب</h4>
              </div>
              <div className="request-details-grid">
                <div className="detail-item">
                  <span className="detail-label">نوع العقار</span>
                  <span className="detail-value">{getPropertyType(selectedRequest.propertyType)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{t('requests.type')}</span>
                  <span className="detail-value">{getRequestType(selectedRequest.requestType)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{t('requests.location')}</span>
                  <span className="detail-value">
                    {selectedRequest.city || '-'} 
                    {selectedRequest.districts && selectedRequest.districts.length > 0 
                      ? ` - ${selectedRequest.districts.join('، ')}` 
                      : selectedRequest.district ? ` - ${selectedRequest.district}` : ''}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{t('requests.budget')}</span>
                  <span className="detail-value">
                    {formatPrice(selectedRequest.budgetValue || selectedRequest.budget || 0)} {t('common.sar')}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">{t('requests.rooms')}</span>
                  <span className="detail-value">{selectedRequest.rooms || '-'}</span>
                </div>
                {selectedRequest.requestType === 'buy' && selectedRequest.paymentMethod && (
                  <div className="detail-item">
                    <span className="detail-label">طريقة الدفع</span>
                    <span className="detail-value">
                      {selectedRequest.paymentMethod === 'bank' ? 'تمويل بنكي' : 'كاش'}
                    </span>
                  </div>
                )}
                {selectedRequest.requestType === 'rent' && selectedRequest.paymentPlan && (
                  <div className="detail-item">
                    <span className="detail-label">خطة الدفع</span>
                    <span className="detail-value">
                      {selectedRequest.paymentPlan === 'single' ? 'دفعة واحدة' :
                       selectedRequest.paymentPlan === 'installments' ? 'دفعات' :
                       selectedRequest.paymentPlan === 'monthly' ? 'شهري' : '-'}
                    </span>
                  </div>
                )}
                {selectedRequest.age && (
                  <div className="detail-item">
                    <span className="detail-label">عمر العقار المطلوب</span>
                    <span className="detail-value">{selectedRequest.age} سنة</span>
                  </div>
                )}
                {selectedRequest.requestType === 'rent' && selectedRequest.floor && selectedRequest.floor !== 'any' && (
                  <div className="detail-item">
                    <span className="detail-label">الدور المطلوب</span>
                    <span className="detail-value">
                      {selectedRequest.floor === 'ground' ? 'أرضي' :
                       selectedRequest.floor === 'first' ? 'أول' :
                       selectedRequest.floor === 'second' ? 'ثاني' :
                       selectedRequest.floor === 'third' ? 'ثالث' :
                       selectedRequest.floor === 'fourth_plus' ? 'رابع فأكثر' :
                       selectedRequest.floor === 'roof' ? 'ملحق' : '-'}
                    </span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">{t('common.date')}</span>
                  <span className="detail-value">{formatDate(selectedRequest.createdAt)}</span>
                </div>
              </div>

              {/* Requirements */}
              {(selectedRequest.notes || selectedRequest.requirements) && (
                <>
                  <div className="section-divider">
                    <h4>المتطلبات / الملاحظات</h4>
                  </div>
                  <div className="notes-section">
                    <p>{selectedRequest.notes || selectedRequest.requirements}</p>
                  </div>
                </>
              )}

              {/* Marketer Info */}
              <div className="section-divider">
                <h4>{t('requests.marketerInfo')}</h4>
              </div>
              <div className="marketer-section">
                <div className="marketer-info">
                  <div className="marketer-avatar">
                    {selectedRequest.marketer?.name?.charAt(0) || '?'}
                  </div>
                  <span>{selectedRequest.marketer?.name || 'غير معروف'}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {selectedRequest.status === 'active' && (
                <>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleUpdateStatus(selectedRequest.id, 'contacted')}
                  >
                    <Phone size={18} />
                    {t('requests.markContacted')}
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={() => handleUpdateStatus(selectedRequest.id, 'completed')}
                  >
                    <CheckCircle size={18} />
                    {t('requests.markCompleted')}
                  </button>
                </>
              )}
              {selectedRequest.status === 'contacted' && (
                <button
                  className="btn btn-success"
                  onClick={() => handleUpdateStatus(selectedRequest.id, 'completed')}
                >
                  <CheckCircle size={18} />
                  {t('requests.markCompleted')}
                </button>
              )}
              <button className="btn btn-outline" onClick={() => setShowRequestModal(false)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestsManagementPage;
